import React from 'react';
import { useSelector } from 'react-redux';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import PropTypes from 'prop-types';
import CardPanel from '../../../shared/components/widgets/CardPanel';

// Icon mapping for each retailer
const RETAILER_ICONS = {
  multi: 'fas fa-layer-group',
  amazon: 'fab fa-amazon',
  walmart: 'fas fa-store',
  target: 'fas fa-bullseye',
  google: 'fab fa-google',
  microsoft: 'fab fa-microsoft',
  meta: 'fab fa-meta',
  tiktok: 'fab fa-tiktok',
  linkedin: 'fab fa-linkedin',
  instagram: 'fab fa-instagram',
  facebook: 'fab fa-facebook',
  instacart: 'fas fa-shopping-basket',
  doordash: 'fas fa-motorcycle',
  uber: 'fab fa-uber',
  gopuff: 'fas fa-truck',
  cvs: 'fas fa-prescription-bottle',
  walgreens: 'fas fa-pills',
  rite_aid: 'fas fa-capsules',
  chewy: 'fas fa-paw',
  macys: 'fas fa-shopping-bag',
  nordstroms: 'fas fa-tshirt',
  the_home_depot: 'fas fa-hammer',
  ace_hardware: 'fas fa-tools',
  michaels: 'fas fa-palette',
  hobby_lobby: 'fas fa-paint-brush',
  sephora: 'fas fa-spray-can',
  kroger: 'fas fa-carrot',
  albertsons: 'fas fa-apple-alt',
  meijer: 'fas fa-shopping-cart',
  whole_foods: 'fas fa-leaf',
  publix: 'fas fa-bread-slice',
  youtube: 'fab fa-youtube',
  youTube_tv: 'fab fa-youtube',
  disney_plus: 'fas fa-tv',
  apple_plus: 'fab fa-apple',
  paramount_plus: 'fas fa-film',
  peacock: 'fas fa-feather',
  netflix: 'fas fa-video',
  tubo: 'fas fa-play-circle',
  freevee: 'fas fa-play',
  sky: 'fas fa-satellite-dish',
};

// Color schemes for retailer categories
const CATEGORY_COLORS = {
  TOOLS: '#8e44ad',
  RETAIL: '#3498db',
  'SOCIAL MEDIA': '#e74c3c',
  DELIVERY: '#f39c12',
  PHARMACY: '#16a085',
  'SPECIALTY RETAIL': '#9b59b6',
  GROCERY: '#27ae60',
  NETWORK: '#e67e22',
};

const RetailerSelectionGrid = ({ retailerOptions, configuredRetailers, budgetGroupCounts, onRetailerSelect }) => {
  const theme = useSelector(store => store.theme);
  const isDarkMode = theme.className && !theme.className.endsWith('light');
  const [selectedGroup, setSelectedGroup] = React.useState('All');

  // Flatten retailer options and categorize
  const allRetailers = [];
  retailerOptions.forEach((group) => {
    const category = group.optgroup.label;
    const categoryColor = CATEGORY_COLORS[category] || '#95a5a6';

    group.optgroup.options.forEach((option) => {
      allRetailers.push({
        ...option,
        category,
        categoryColor,
        isConfigured: configuredRetailers.includes(option.value),
      });
    });
  });

  // Sort: configured first, then unconfigured
  const sortedRetailers = [
    ...allRetailers.filter(r => r.isConfigured),
    ...allRetailers.filter(r => !r.isConfigured),
  ];

  // Filter by selected group
  const filteredRetailers = selectedGroup === 'All'
    ? sortedRetailers
    : sortedRetailers.filter(r => r.category === selectedGroup);

  // Get unique platform groups for dropdown
  const platformGroups = ['All', ...new Set(retailerOptions.map(g => g.optgroup.label))];

  const handleCardClick = (retailer) => {
    if (retailer.isConfigured) {
      onRetailerSelect(retailer.value);
    }
  };

  return (
    <Container className="dashboard">
      <Row className="mb-4">
        <CardPanel showButtons={false}>
          <Row>
            <Col md={12}>
              <h3 className="page-title text-center">Select a Retailer Platform to Manage</h3>
              <p className="text-center text-muted">
                <span style={{ marginRight: '10px', fontSize: '0.9rem' }}>Filter by Platform Group:</span>
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  className="select-options"
                  style={{ width: 'auto', minWidth: '200px' }}
                >
                  {platformGroups.map((group) => (
                    <option key={group} value={group}>
                      {group.replace(':', '')}
                    </option>
                  ))}
                </select>
              </p>
            </Col>
          </Row>
        </CardPanel>
      </Row>
      <Row>
        <Col md={12}>
          <CardPanel showButtons={false}>
            <Row>
              {filteredRetailers.map((retailer) => {
                const icon = RETAILER_ICONS[retailer.value] || 'fas fa-shopping-cart';
                const { isConfigured } = retailer;
                const budgetCount = isConfigured ? (budgetGroupCounts[retailer.value] || 0) : 0;
                const textColor = isDarkMode ? '#ddd' : '#333';
                const mutedColor = isDarkMode ? '#999' : '#777';

                return (
                  <Col key={retailer.value} xs={12} sm={6} md={4} lg={3} className="mb-4">
                    <Card
                      className={`retailer-card ${isConfigured ? 'retailer-card--active' : 'retailer-card--disabled'}`}
                      onClick={() => handleCardClick(retailer)}
                      style={{
                        cursor: isConfigured ? 'pointer' : 'not-allowed',
                        opacity: isConfigured ? 1 : 0.5,
                        transition: 'all 0.3s ease',
                        border: `2px solid ${isConfigured ? retailer.categoryColor : '#e0e0e0'}`,
                        minHeight: '220px',
                      }}
                    >
                      <CardBody className="d-flex flex-column justify-content-between" style={{ padding: '1.5rem' }}>
                        {/* Platform name in upper left */}
                        <div style={{ textAlign: 'left', marginBottom: '1rem' }}>
                          <h5 style={{ color: isConfigured ? textColor : '#999', fontWeight: 'bold', fontSize: '1.2rem', margin: 0 }}>
                            {retailer.label}
                          </h5>
                        </div>

                        {/* Logo centered at top */}
                        <div className="text-center mb-3" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className={icon} style={{ fontSize: '4rem', color: isConfigured ? retailer.categoryColor : '#999' }} />
                        </div>

                        {/* Stats Section */}
                        <div>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong style={{ fontSize: '0.75rem', color: mutedColor }}>Platform Group</strong>
                            <strong style={{ fontSize: '0.75rem', color: mutedColor }}>Budget Groups</strong>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <div style={{ fontSize: '1rem', color: mutedColor }}>
                              {retailer.category.replace(':', '')}
                            </div>
                            <div className="text-right">
                              {isConfigured ? (
                                <strong style={{ color: retailer.categoryColor, fontSize: '1.5rem' }}>
                                  {budgetCount}
                                </strong>
                              ) : (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: '#95a5a6',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                  }}
                                >
                                  Coming Soon
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </CardPanel>
        </Col>
      </Row>
    </Container>
  );
};

RetailerSelectionGrid.propTypes = {
  retailerOptions: PropTypes.arrayOf(
    PropTypes.shape({
      optgroup: PropTypes.shape({
        label: PropTypes.string.isRequired,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
          }),
        ).isRequired,
      }).isRequired,
    }),
  ).isRequired,
  configuredRetailers: PropTypes.arrayOf(PropTypes.string).isRequired,
  budgetGroupCounts: PropTypes.objectOf(PropTypes.number).isRequired,
  onRetailerSelect: PropTypes.func.isRequired,
};

export default RetailerSelectionGrid;
